import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { configuration } from "../configuration/ConfigurationService";
import { InteractionStatus } from "@azure/msal-browser";

import { appSessionStorage } from "../storage/SessionStorageService";
import { VIEWER_TOKEN_KEY } from "./AuthService";
import { removeMsalCacheEntries } from "./msalCacheCleanup";
import { makeAutoObservable } from "mobx";

// Timestamp of the last interactive-recovery redirect (sessionStorage). A short
// cooldown blocks the rapid silent→redirect loop seen on browsers that block
// third-party cookies (Edge default), while NOT locking recovery out for the whole
// tab session if a sign-in is dismissed. Cleared on a successful token acquisition
// and on logout (appSessionStorage.clear()).
const INTERACTIVE_RETRY_KEY = "msalInteractiveRetryAt";
const INTERACTIVE_RETRY_COOLDOWN_MS = 15000;

// MSAL error codes that mean "silent renewal can't proceed — interactive auth is
// required". Checked by string (not `instanceof`) because Authentication.ts (in the
// shared webclient) and the app's PublicClientApplication resolve to DIFFERENT copies
// of @azure/msal-browser via the file:-linked node_modules, so `instanceof` against
// the imported class is always false. The error's `.errorCode`/`.name` are stable
// across copies.
const INTERACTION_REQUIRED_CODES = [
  "interaction_required",
  "login_required",
  "consent_required",
  "no_tokens_found",
  "native_account_unavailable",
];

// Where to send the user after a login that was triggered by opening an app while signed
// out (guest-home model). Set by loginToAccess() before the redirect; read once by the
// app after auth as a defensive fallback to redirectStartPage.
export const POST_LOGIN_ROUTE_KEY = "postLoginRoute";

export interface Authentication {
  logout: () => void;
  getAccessToken: () => Promise<string>;
  loginAndObtainToken: (
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => void;
  // Public-home-page boot: resolve auth state without ever forcing a login.
  resolveSession: (
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => void;
  // Trigger interactive login when a signed-out user opens an app, returning to `route`.
  loginToAccess: (route: string) => void;
  // Drop the cached MSAL session locally, so a wedged boot can be recovered without the user
  // having to clear localStorage by hand.
  resetLocalSession: () => Promise<void>;
}

export class AuthenticationService implements Authentication {
  private msalInstance: IPublicClientApplication | undefined;
  viewerAuthToken: string | null | undefined;
  userAccount: AccountInfo | null | undefined;
  loginInProgress: InteractionStatus | undefined;
  private resolveCallback: Array<(token: string) => void> = [];
  private needsSetup: boolean = true;
  public anonymous: boolean = true;
  // Internal request-dedup plumbing (not reactive state); no component observes it.
  private appTokenPromise: Promise<string> | null = null;
  // Whether a failed silent renewal may auto-redirect to interactive login. Apps using
  // loginAndObtainToken() keep this true (legacy forced-login recovery). resolveSession()
  // sets it false so the public-home-page app drops to guest instead of redirecting.
  private autoRecoverOnInteractionRequired: boolean = true;

  constructor() {
    makeAutoObservable(this)
  }

  loginAndObtainToken = async (
    msalInstance: any,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => {
    this.setAuthProperties(msalInstance, userAccount, viewerAuthToken, loginInProgress);

    const accessToken = await this.getAccessToken();
    this.sendAccessToken(accessToken);
  };

  setAnonymous = (anonymous: boolean) => {
    this.anonymous = anonymous;
  }

  // Public-home-page boot entry: never forces a login. Auth is decided by whether a token
  // can actually be obtained — a valid cached session logs in silently; a stale account
  // with a dead session drops to GUEST (anonymous) with NO redirect; no account = guest.
  // Interactive login happens later, only via loginToAccess() when opening an app.
  resolveSession = async (
    // `any` (like loginAndObtainToken) because the app and this shared lib bundle
    // different @azure/msal-browser copies, so their IPublicClientApplication types differ.
    msalInstance: any,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => {
    this.msalInstance = msalInstance;
    this.userAccount = userAccount;
    this.viewerAuthToken = viewerAuthToken;
    this.loginInProgress = loginInProgress;
    this.needsSetup = false;
    this.autoRecoverOnInteractionRequired = false;

    if (viewerAuthToken) {
      appSessionStorage.setStorageItem(VIEWER_TOKEN_KEY, viewerAuthToken as string);
      this.setAnonymous(false);
      this.sendAccessToken(viewerAuthToken);
      return;
    }
    if (userAccount) {
      const token = await this.getAppAuthenticationToken();
      this.setAnonymous(!token);
      if (token) {
        this.sendAccessToken(token);
      } else {
        // A cached account whose B2C session is dead. Evict it, or every subsequent page load
        // repeats this same dead-token path: MSAL keeps handing the account back, the app keeps
        // resolving as "signed in", and the user is left clearing localStorage by hand.
        // Dropping the account makes the next load a clean guest boot.
        await this.forgetStaleAccount(userAccount);
      }
      return;
    }
    this.setAnonymous(true);
  };

  private forgetStaleAccount = async (account: AccountInfo) => {
    await this.clearMsalCache(account);
  };

  /**
   * Drop the locally cached MSAL session so the next load starts as a clean guest.
   *
   * This is the supported alternative to clearing localStorage by hand, which is what a wedged
   * boot used to require.
   */
  resetLocalSession = async () => {
    await this.clearMsalCache();
    this.setAnonymous(true);
  };

  /**
   * Clear the MSAL cache without sending the user to B2C.
   *
   * v3+ exposes `logoutSilent`, which is the supported local-only logout; prefer it when the
   * bundled copy has it.
   *
   * The installed msal-browser is **v2**, which has no local-only removal API. `logoutRedirect`
   * with `onRedirectNavigate: () => false` looks like the answer but is a NAVIGATION api: called
   * during boot it races the in-flight `handleRedirectPromise` and rejects with a plain
   * `BrowserAuthError` ("interaction_in_progress") — which the dev overlay renders as the
   * useless "[object Object]". So on v2 the cache entries are removed directly: synchronous, no
   * navigation, and nothing to conflict with the interaction state.
   *
   * Best-effort throughout: a failure must never block the boot — the caller has already
   * dropped to guest, and this only stops the situation recurring on the next load.
   */
  private clearMsalCache = async (account?: AccountInfo) => {
    const instance = this.msalInstance as any;
    if (typeof instance?.logoutSilent === "function") {
      try {
        await instance.logoutSilent(account ? { account } : {});
        return;
      } catch (error) {
        console.error("logoutSilent failed, falling back to cache removal:", error);
      }
    }
    removeMsalCacheEntries(account?.homeAccountId);
  };

  // Interactive login triggered when a signed-out user opens an app. Full-page redirect to
  // B2C; returns to `route` afterward (redirectStartPage + a stored fallback). Guarded so a
  // concurrent MSAL interaction doesn't throw.
  loginToAccess = async (route: string) => {
    if (!this.msalInstance) return;
    if (this.loginInProgress !== undefined && this.loginInProgress !== InteractionStatus.None) return;
    const config = await configuration.get();
    const path = route.startsWith("/") ? route : `/${route}`;
    appSessionStorage.setStorageItem(POST_LOGIN_ROUTE_KEY, path);
    await this.msalInstance.loginRedirect({
      scopes: config?.scopes ?? [],
      redirectStartPage: window.location.origin + path,
    });
  };

  private setAuthProperties(
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) {
    this.msalInstance = msalInstance;
    this.userAccount = userAccount;
    this.viewerAuthToken = viewerAuthToken;
    this.loginInProgress = loginInProgress;
    this.needsSetup = false;
    this.anonymous = false;
  }

  getAccessToken = async () => {
    if (this.anonymous) {
      return ""
    }
    if (this.needsSetup) {
      return new Promise<string>((resolve, _) => {
        this.resolveCallback.push(resolve);
      });
    } else {
      if (this.viewerAuthToken) {
        appSessionStorage.setStorageItem(VIEWER_TOKEN_KEY, this.viewerAuthToken as string);
        return this.viewerAuthToken;
      } else {
        this.login();
        const token = await this.getAppAuthenticationToken();
        return token;
      }
    }
  };

  private login = async () => {
    if (
      !this.viewerAuthToken &&
      !this.userAccount &&
      this.loginInProgress === InteractionStatus.None
    ) {
      // Pass the API scope so the access token is acquired during the interactive
      // redirect (not later via a hidden iframe). Silent renewal via *.b2clogin.com
      // fails on browsers that block third-party cookies (Edge default), which would
      // trigger acquireTokenSilent → catch → loginRedirect → loop.
      const config = await configuration.get();
      await this.msalInstance?.loginRedirect({
        scopes: config?.scopes ?? [],
      });
    }
  };

  private getAppAuthenticationToken = async (): Promise<string> => {
    // De-dupe concurrent callers: the ~6 storage models that auto-load on boot each
    // call getAccessToken → here. Share a single in-flight request so we issue one
    // acquireTokenSilent (and at most one recovery redirect) instead of ~6. The
    // promise is cleared once it settles, so a later legitimate request can still
    // retry — failures are never memoized.
    if (this.appTokenPromise) return this.appTokenPromise;
    this.appTokenPromise = this.acquireAppToken().finally(() => {
      this.appTokenPromise = null;
    });
    return this.appTokenPromise;
  };

  private acquireAppToken = async (): Promise<string> => {
    let config = await configuration.get();
    if (this.userAccount && config.scopes && this.msalInstance) {
      try {
        const response = await this.msalInstance.acquireTokenSilent({
          scopes: config.scopes,
          account: this.userAccount,
        })
        // Silent renewal worked — reset the recovery cooldown.
        appSessionStorage.removeStorageItem(INTERACTIVE_RETRY_KEY);
        return response.accessToken;
      } catch (error) {
        // Silent renewal failed because the B2C session is gone / the refresh token is
        // invalid (interaction_required / AADB2C90077). Recover with a full-page
        // (first-party) interactive redirect — unlike the hidden-iframe silent flow it
        // does NOT depend on third-party cookies. Detect the error by code/name (NOT
        // `instanceof`, which is unreliable across the two bundled msal-browser copies),
        // and rate-limit via a cooldown so it can't spin in a redirect loop.
        const errorCode = (error as { errorCode?: string })?.errorCode ?? "";
        const errorName = (error as { name?: string })?.name ?? "";
        const isInteractionRequired =
          errorName === "InteractionRequiredAuthError" ||
          INTERACTION_REQUIRED_CODES.includes(errorCode);
        const lastAttempt = Number(appSessionStorage.getStorageItem(INTERACTIVE_RETRY_KEY)) || 0;

        if (
          this.autoRecoverOnInteractionRequired &&
          isInteractionRequired &&
          this.loginInProgress === InteractionStatus.None &&
          Date.now() - lastAttempt > INTERACTIVE_RETRY_COOLDOWN_MS
        ) {
          appSessionStorage.setStorageItem(INTERACTIVE_RETRY_KEY, String(Date.now()));
          try {
            await this.msalInstance.acquireTokenRedirect({
              scopes: config.scopes,
              account: this.userAccount,
            });
          } catch (redirectError) {
            console.error("MSAL interactive recovery failed:", redirectError);
          }
          return ""; // the redirect navigates away; nothing more to do here
        }
        console.error("MSAL acquireTokenSilent failed:", error);
      }
    }
    return "";
  };

  private sendAccessToken = (token: string) => {
    if (this.resolveCallback && token) {
      this.resolveCallback.forEach((resolve) => resolve(token));
    }
  };

  logout = () => {
    appSessionStorage.clear();
    if (this.msalInstance) this.msalInstance.logout();
  };
}
export const authentication = new AuthenticationService();
